import axios from 'axios';
import { auth } from '../firebase';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Token cache with expiration
let tokenCache = {
  token: null,
  expiration: null
};

// Function to get a fresh token with caching
const getFreshToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user logged in');
  }

  try {
    // Check if we have a cached token that's still valid (with 5-minute buffer)
    const now = Date.now();
    if (tokenCache.token && tokenCache.expiration && now < tokenCache.expiration - 300000) {
      return tokenCache.token;
    }

    // Get fresh token
    const token = await user.getIdToken(false); // Don't force refresh if not needed
    
    // Cache the token with 1-hour expiration
    tokenCache.token = token;
    tokenCache.expiration = now + 3600000; // 1 hour
    
    return token;
  } catch (error) {
    console.error('Error getting fresh token:', error);
    // Clear cache on error
    tokenCache.token = null;
    tokenCache.expiration = null;
    throw error;
  }
};

// Add request interceptor to add Authorization header and handle API version
api.interceptors.request.use(async (config) => {
  try {
    // Get token (cached if available)
    const user = auth.currentUser;
    if (user) {
      const token = await getFreshToken();
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Don't modify URLs that already have /api/v1/
    if (!config.url.includes('/api/v1/')) {
      config.url = `/api/v1${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }

    return config;
  } catch (error) {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
});

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Force token refresh and retry request
        const token = await getFreshToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return await axios(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
